import React, { useEffect, useState } from 'react';
import { GET_LIST, GET_ONE, authHeader, provider } from '../../_utils';
import { motivationLabel } from '../motivation/formula';

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const unwrap = (payload) => {
    if (!payload) return null;
    if (payload.workingDays != null || payload.normFullHours != null) return payload;
    if (payload.data && typeof payload.data === 'object') return payload.data;
    return payload;
};

const CalculateModal = ({ open, period, onClose, onStart }) => {
    const [versions, setVersions] = useState([]);
    const [motivationId, setMotivationId] = useState('');
    const [regenerate, setRegenerate] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(period);
    const [calendar, setCalendar] = useState(null);
    const [normFullHours, setNormFullHours] = useState('');

    useEffect(() => {
        if (!open) return;
        setSelectedPeriod(period);
        const load = async () => {
            try {
                const list = await provider(GET_LIST, '/operators/motivation', {
                    filter: {},
                    sort: { field: 'version', order: 'DESC' },
                    pagination: { page: 1, perPage: 200 },
                });
                const rows = list.status >= 200 && list.status < 300 ? (list.data || []) : [];
                setVersions(rows);
                const current = await provider(GET_ONE, '/operators/motivation', 'current');
                const currentId = current.status >= 200 && current.status < 300
                    ? (current.data?._id || current.data?.id)
                    : (rows.find((row) => row.status === 'active')?._id || rows[0]?._id || '');
                setMotivationId(currentId || '');
            } catch (error) {
                setVersions([]);
            }
        };
        load();
    }, [open, period]);

    useEffect(() => {
        if (!open || !selectedPeriod) return undefined;
        let cancelled = false;
        const loadCalendar = async () => {
            try {
                const response = await fetch(
                    `/api/operators/payroll-calendar?period=${encodeURIComponent(selectedPeriod)}`,
                    {
                        credentials: 'same-origin',
                        headers: { ...authHeader(), Accept: 'application/json' },
                    }
                );
                const json = await response.json().catch(() => ({}));
                const data = unwrap(json);
                if (cancelled) return;
                if (response.ok && data) {
                    setCalendar(data);
                    if (data.normFullHours != null && data.normFullHours !== '') {
                        setNormFullHours(String(data.normFullHours));
                    }
                } else {
                    setCalendar({
                        warning: json.message || 'Не удалось загрузить производственный календарь. Укажите норму вручную.',
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    setCalendar({
                        warning: 'Не удалось загрузить производственный календарь. Укажите норму вручную.',
                    });
                }
            }
        };
        loadCalendar();
        return () => { cancelled = true; };
    }, [open, selectedPeriod]);

    if (!open) return null;

    const normValue = Number(normFullHours);
    const canRun = Boolean(selectedPeriod) && Number.isFinite(normValue) && normValue > 0;

    const run = () => {
        if (!canRun || !onStart) return;
        onStart({
            period: selectedPeriod,
            motivationId,
            regenerate,
            normFullHours: normValue,
        });
        onClose();
    };

    return (
        <div className="pc-emp-ov" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="pc-emp-modal pc-emp-modal--sm" role="dialog" aria-modal="true" aria-labelledby="pc-calc-title">
                <div className="pc-emp-mh">
                    <div>
                        <h3 id="pc-calc-title">Расчёт заработной платы</h3>
                        <div className="sub">Запускается только по кнопке. Операторы с типом «Обучение» в расчёт не входят.</div>
                    </div>
                    <button type="button" className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet" onClick={onClose} title="Закрыть">
                        <Icon d="M4 4l8 8M12 4l-8 8" />
                    </button>
                </div>
                <div className="pc-emp-mb">
                    <div className="pc-emp-fg">
                        <label className="pc-emp-fl">
                            <span>Период</span>
                            <input
                                className="pc-emp-inp pc-emp-inp--period"
                                type="month"
                                value={selectedPeriod}
                                onChange={(e) => setSelectedPeriod(e.target.value)}
                            />
                        </label>
                        <label className="pc-emp-fl">
                            <span>Версия мотивации</span>
                            <select className="pc-emp-inp" value={motivationId} onChange={(e) => setMotivationId(e.target.value)}>
                                <option value="">Активная на момент расчёта</option>
                                {versions.map((row) => {
                                    const id = row._id || row.id;
                                    return (
                                        <option key={id} value={id}>{motivationLabel(row)}</option>
                                    );
                                })}
                            </select>
                        </label>
                        <label className="pc-emp-fl">
                            <span>Норма полной ставки, ч *</span>
                            <input
                                className="pc-emp-inp pc-emp-inp--mono"
                                type="number"
                                step="0.5"
                                min="0.5"
                                value={normFullHours}
                                onChange={(e) => setNormFullHours(e.target.value)}
                            />
                        </label>
                        <label className="pc-emp-check pc-emp-fl--2">
                            <input type="checkbox" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} />
                            Пересчитать уже существующие листы периода
                        </label>
                    </div>
                    <p className="pc-mot-hint">Норма месяца подставляется из производственного календаря. Её можно поправить вручную.</p>
                    {calendar?.warning || (calendar && calendar.holidays === 0 && calendar.workingWeekends === 0) ? (
                        <div className="pc-emp-note" style={{ marginTop: 10 }}>
                            {calendar.warning || 'Календарь исключений за месяц пуст'}
                        </div>
                    ) : null}
                </div>
                <div className="pc-emp-mf">
                    <button type="button" className="pc-emp-btn pc-emp-btn--pri" disabled={!canRun} onClick={run}>
                        Запустить расчёт
                    </button>
                    <button type="button" className="pc-emp-btn" onClick={onClose}>Отмена</button>
                </div>
            </div>
        </div>
    );
};

export default CalculateModal;
